import { Note } from "../shared/note.model";
import { NoteTemplateValue } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { Utils } from "ng/src/app/core/utils";
import { TreeNode } from "primeng/api";
import { LogService } from "ng/src/app/logs/log.service";
import { LogUser } from "../../users/shared/user.model";

export interface DraftNotesRowData {
	id: string
	name: string,
	notes: NoteWithKeyValues[],
	hasKeys: boolean,
	selected: boolean,
	logId: number,
}

export interface NoteWithKeyValues extends Note {
	keyValues: string[],
	logName: string,
}

interface CustomTreeNode<T = any, C extends TreeNode = TreeNode> extends TreeNode {
	data: T,
	children: C[]
}
export type LogTreeNode = CustomTreeNode<LogData, TemplateTreeNode>;
export type TemplateTreeNode = CustomTreeNode<TemplateData, NoteTreeNode>;
export type NoteTreeNode = CustomTreeNode<NoteData>;

export interface LogData {
	keyValues: string,
	id: number
}

export interface TemplateData {
	keyValues: string,
	id: number
}

export interface NoteData {
	keyValues: string[],
	coCreators: LogUser[],
	created: string,
	note: NoteWithKeyValues,
	logIx: number,
	templateIx: number,
	noteIx: number
}

export class DraftNotesRowData {
	
	static createFrom(notes: Note.Projections.KeyNoteTemplateValues[], expandLargeCollections = false): LogTreeNode[]
	{
		const logMap: { [logId: number]: {[templateId: number]: NoteWithKeyValues[]}} = {};
		
		notes.forEach(note => {
			if (!logMap[note.logId]) logMap[note.logId] = {};
			if (!logMap[note.logId][note.templateId!]) logMap[note.logId][note.templateId!] = [];
			const drafts: NoteWithKeyValues[] = logMap[note.logId][note.templateId!] ?? [];

			// sort co-creators & template values
			note.coCreators.sort(Utils.createMultiSortFunction([
				[u => u.user.lastName, 'asc'],
				[u => u.user.firstName, 'asc'],
			]));
			note.templateValues.sort(Utils.createSortFunction(tv => tv.templateSegmentSortOrder ?? -1));
			
			drafts.push({
				...note,
				keyValues: getValues(note.templateValues)
			});

			logMap[note.logId][note.templateId!] = drafts;
		})

		return Object.keys(logMap).map((logId, logIx) => {
			const logData = logMap[parseInt(logId)];
			const log = logData[parseInt(Object.keys(logData)[0])][0];
			return {
				data: { keyValues: log.logName, id: log.logId },
				expanded: (logId === LogService.selectedLog?.id?.toString()) ?? expandLargeCollections,
				children: Object.keys(logData).map((templateId, templateIx) => {
					const templateData = logData[parseInt(templateId)];
					const note = templateData[0];
					return {
						data: { keyValues: note.templateName!, id: note.templateId! },
						expanded: expandLargeCollections || templateData.length <= 3,
						children: templateData.map((note, noteIx) => ({
							children: [],
							data: {
								keyValues: note.keyValues,
								coCreators: note.coCreators,
								created: note.created,
								note,
								logIx,
								templateIx,
								noteIx
							},
						}))
					}
				}),
			}
		});
		
		function getValues(ntvs: NoteTemplateValue[]) {
			return ntvs.map(ntv => {
					if (ntv.tagValues && ntv.tagValues.length > 0) {
						return ntv.tagValues.map(tv => tv.stringValue).join(',')
					}
					
					return ntv.stringValue ?? ''
				})
				.filter(str => str.trim().length > 0)
		}
	}

	static updateIndexes(nodes: LogTreeNode[]) {
		nodes.forEach((logNode, logNodeIdx) => {
			logNode.children.forEach((templateNode, templateNodeIdx) => {
				templateNode.children.forEach((noteNode, noteNodeIdx) => {
					noteNode.data.logIx = logNodeIdx;
					noteNode.data.templateIx = templateNodeIdx;
					noteNode.data.noteIx = noteNodeIdx;
				});
			});
		});
		return nodes;
	}
	
}